import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useResumeTailoring } from '@/contexts/ResumeTailoringContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, ArrowLeft, Loader2, FileText, Briefcase, StickyNote, Wand2, Coins, Zap } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useCredits } from '@/hooks/useCredits';
import { MultiDocumentTailoringService } from '@/services/multiDocumentTailoringService';
import { supabase } from '@/integrations/supabase/client';
import { UpgradePrompt } from '@/components/UpgradePrompt';
import { StepHeader } from './StepHeader';

const DOC_LABELS: Record<string, string> = {
  'resume': 'Resume',
  'cover-letter': 'Cover Letter',
  'linkedin-profile': 'LinkedIn Profile',
  'executive-bio': 'Executive Bio',
  'thank-you-letter': 'Thank You Letter',
  'outreach-email': 'Outreach Email',
};

const CREDIT_COSTS: Record<string, number> = {
  'resume': 5,
  'cover-letter': 3,
  'linkedin-profile': 4,
  'executive-bio': 3,
  'thank-you-letter': 0,
  'outreach-email': 0,
};

export function ReviewGeneratePage() {
  const navigate = useNavigate();
  const { state, dispatch } = useResumeTailoring();
  const { user } = useAuth();
  const { toast } = useToast();
  const { balance, deductCredits, isLoading: creditsLoading } = useCredits();

  const [isGenerating, setIsGenerating] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  const totalCredits = state.selectedDocumentTypes.reduce(
    (sum, t) => sum + (CREDIT_COSTS[t] ?? 0), 0
  ) || 5;

  const handleGenerate = async () => {
    if (!user) {
      toast({ title: 'Please log in to generate documents.', variant: 'destructive' });
      return;
    }
    if (balance < totalCredits) { setShowUpgradePrompt(true); return; }

    setIsGenerating(true);
    const deducted = await deductCredits('resume_tailoring', {
      documentsGenerated: state.selectedDocumentTypes,
      jobTitle: state.jobPositionTitle,
    });
    if (!deducted.success) { setIsGenerating(false); setShowUpgradePrompt(true); return; }

    dispatch({ type: 'SET_PROCESSING', payload: true });

    try {
      // Try custom pipeline first
      const { data: settingsRow } = await supabase
        .from('app_settings').select('value').eq('key', 'simple_pipeline_config').single();

      if (settingsRow?.value) {
        const session = await supabase.auth.getSession();
        const token = session.data.session?.access_token;
        const res = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/run-custom-pipeline`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string,
            },
            body: JSON.stringify({
              resume: state.processedResumeData,
              job_description: state.jobDescription,
              special_instructions: state.additionalInstructionsText || 'None',
            }),
          }
        );
        const result = await res.json();
        if (!res.ok || !result.success) throw new Error(result.error || 'Pipeline failed');
        dispatch({ type: 'SET_RESULTS', payload: { tailoredResumeContent: result.final_resume } });
        toast({ title: `Pipeline "${result.pipeline_name}" complete` });
        navigate('/resume-tailoring-simple/step/5');
        return;
      }

      // No pipeline — use standard service
      const response = await MultiDocumentTailoringService.generateMultipleDocuments({
        resumeText: state.processedResumeData,
        jobDescription: state.jobDescription,
        selectedDocumentTypes: state.selectedDocumentTypes,
        jobTitle: state.jobPositionTitle,
        companyName: '',
        sectionsToTailor: state.sectionsToTailor,
        quantificationLevel: state.quantificationLevel,
        writingStyle: state.writingStyle,
        additionalInstructions: state.additionalInstructionsText,
      });

      dispatch({
        type: 'SET_RESULTS',
        payload: {
          tailoredResumeContent: response.tailoredResumeContent,
          coverLetterContent: response.coverLetterContent,
          linkedinContent: response.linkedinContent,
          thankYouLetterContent: response.thankYouLetterContent,
          outreachEmailContent: response.outreachEmailContent,
          executiveBioContent: response.executiveBioContent,
          analysisData: response.analysisData,
        },
      });
      toast({ title: 'Documents generated!' });
      navigate('/resume-tailoring-simple/step/5');
    } catch (error) {
      toast({
        title: 'Generation failed',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
      dispatch({ type: 'SET_PROCESSING', payload: false });
    }
  };

  const summaryItems = [
    {
      icon: FileText,
      label: 'Resume',
      value: state.selectedResumeId
        ? 'From library'
        : state.uploadedFile?.name
        ? state.uploadedFile.name
        : state.pastedResumeText
        ? `${state.pastedResumeText.slice(0, 40)}…`
        : '—',
    },
    {
      icon: Briefcase,
      label: 'Position',
      value: state.jobPositionTitle
        ? `${state.jobPositionTitle}${state.jobPositionIndustry ? ` · ${state.jobPositionIndustry}` : ''}`
        : '—',
    },
    {
      icon: FileText,
      label: 'Documents',
      value: state.selectedDocumentTypes.map(t => DOC_LABELS[t] ?? t).join(', ') || '—',
    },
    ...(state.additionalInstructionsText
      ? [{ icon: StickyNote, label: 'Instructions', value: state.additionalInstructionsText.slice(0, 80) + (state.additionalInstructionsText.length > 80 ? '…' : '') }]
      : []),
  ];

  return (
    <div className="w-full max-w-3xl mx-auto px-4 py-8">
      <StepHeader currentStep={4} />

      {/* Summary */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            Review Your Setup
          </CardTitle>
          <CardDescription>Confirm everything looks right before generating</CardDescription>
        </CardHeader>
        <CardContent className="divide-y divide-border">
          {summaryItems.map(({ icon: Icon, label, value }) => (
            <div key={label} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Icon className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</p>
                <p className="text-sm mt-0.5">{value}</p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Credit cost */}
      <Card className="mb-6 border-amber-200 bg-amber-50/50">
        <CardContent className="pt-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Coins className="h-5 w-5 text-amber-600" />
              <div>
                <p className="font-semibold">Credit Cost</p>
                <p className="text-sm text-muted-foreground">This generation will use {totalCredits} credit{totalCredits !== 1 ? 's' : ''}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-amber-600">{totalCredits}</p>
              <p className="text-xs text-muted-foreground">{balance} available</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Generate */}
      <Card className="mb-6 border-primary/20 bg-primary/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            Ready to Generate
          </CardTitle>
          <CardDescription>Your AI-tailored documents will be ready in under 60 seconds</CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={handleGenerate}
            disabled={isGenerating || creditsLoading || balance < totalCredits}
            size="lg"
            className="w-full text-base font-bold"
          >
            {isGenerating ? (
              <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Generating your documents…</>
            ) : balance < totalCredits ? (
              <><Coins className="mr-2 h-5 w-5" /> Insufficient Credits</>
            ) : (
              <><Wand2 className="mr-2 h-5 w-5" /> Generate Documents ({totalCredits} credits)</>
            )}
          </Button>
        </CardContent>
      </Card>

      {showUpgradePrompt && (
        <UpgradePrompt
          variant="modal"
          feature="Resume Tailoring"
          creditsNeeded={totalCredits}
          onDismiss={() => setShowUpgradePrompt(false)}
        />
      )}

      <div className="flex justify-between">
        <Button variant="outline" onClick={() => navigate('/resume-tailoring-simple/step/3')} disabled={isGenerating}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        <div />
      </div>
    </div>
  );
}
